import { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Box,
  Grid,
  Typography,
  CircularProgress,
  Container,
  Chip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Alert,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import LoadingButton from '@mui/lab/LoadingButton';
import { useModal } from '@ebay/nice-modal-react';
import { useSnackbar } from 'notistack';
import carsService, { MarketCheckSearchParams } from '../../api/services/cars';
import { MarketcheckListingsResult } from '../../types/MarketcheckActiveResult';
import CarFilters from './components/CarFilters';
import CarListingRow from './components/CarListingRow';
import CarDetailsDialog from './components/CarDetailsDialog';
import dealsService from '../../api/services/deals';
import offersService from '../../api/services/offers';
import { useAuth } from '../../contexts/AuthContext';
import { annualizedMiles, fleetRisk } from '../../utils/vehicleComparison';

export const MAX_SELECTION = 5;
const PAGE_SIZE = 50;
const SELECTION_KEY = 'dealerscout.selectedCars';

type Listing = MarketcheckListingsResult['listings'][number];

const uniqueArrayValues = (array: any[], key: string): string[] => [
  ...new Set(
    array
      .map((item) => key.split('.').reduce((obj, k) => obj?.[k], item))
      .filter(Boolean) as string[]
  ),
];

const parseParams = (search: string): MarketCheckSearchParams => {
  const p = new URLSearchParams(search);
  const num = (k: string) => (p.get(k) ? Number(p.get(k)) : undefined);
  return {
    make: p.get('make') || '',
    model: p.get('model') || '',
    car_type: p.get('car_type') || 'used',
    radius: num('radius') || 50,
    year: num('year'),
    trim: p.get('trim') || undefined,
    latitude: num('latitude'),
    longitude: num('longitude'),
    zip: p.get('zip') || undefined,
    miles_range: p.get('miles_range') || undefined,
  };
};

const readSavedSelection = (search: string): Listing[] => {
  try {
    const saved = JSON.parse(sessionStorage.getItem(SELECTION_KEY) || 'null');
    return saved?.search === search && Array.isArray(saved.listings)
      ? saved.listings
      : [];
  } catch {
    return [];
  }
};

const Cars = () => {
  const { search } = useLocation();
  const params = useMemo(() => parseParams(search), [search]);
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const { enqueueSnackbar } = useSnackbar();
  const carDetailsModal = useModal(CarDetailsDialog);

  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(true);
  const observer = useRef<IntersectionObserver>();
  const [cars, setCars] = useState<MarketcheckListingsResult | null>(null);
  // Keep whole listings (not just ids) so a login round-trip can restore the selection.
  const [selected, setSelected] = useState<Listing[]>(() =>
    readSavedSelection(search)
  );
  const [sortBy, setSortBy] = useState('distance');
  const [filters, setFilters] = useState({
    bodyType: '',
    transmission: '',
    fuelType: '',
    exteriorColor: '',
    interiorColor: '',
    driveType: '',
    isCertified: '',
  });
  // Default OFF — don't change the default result set (findings §2 is a flag, not a filter).
  const [hideHighMilesForAge, setHideHighMilesForAge] = useState(false);

  const filterOptions = useMemo(() => {
    const listings = cars?.listings ?? [];
    return {
      bodyTypes: uniqueArrayValues(listings, 'build.body_type'),
      transmissions: uniqueArrayValues(listings, 'build.transmission'),
      fuelTypes: uniqueArrayValues(listings, 'build.fuel_type'),
      exteriorColors: uniqueArrayValues(listings, 'exterior_color'),
      interiorColors: uniqueArrayValues(listings, 'interior_color'),
      driveTypes: uniqueArrayValues(listings, 'build.drivetrain'),
    };
  }, [cars?.listings]);

  useEffect(() => {
    sessionStorage.setItem(
      SELECTION_KEY,
      JSON.stringify({ search, listings: selected })
    );
  }, [search, selected]);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const result = await carsService.find({
          ...params,
          start: page * PAGE_SIZE,
          rows: PAGE_SIZE,
        });
        if (cancelled) return;
        setCars((prev) => {
          const seen = new Set(prev?.listings.map((l) => l.id) ?? []);
          const fresh = result.listings.filter((l) => !seen.has(l.id));
          return { ...result, listings: [...(prev?.listings ?? []), ...fresh] };
        });
        setHasMore(result.listings.length === PAGE_SIZE);
      } catch (e: any) {
        if (cancelled) return;
        setError(
          e?.response?.data?.message || 'Unable to load listings right now.'
        );
        setHasMore(false);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [page, params]);

  const lastCarElementRef = useCallback(
    (node: HTMLDivElement | null) => {
      if (loading) return;
      observer.current?.disconnect();
      observer.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && hasMore) {
          setPage((prev) => prev + 1);
        }
      });
      if (node) observer.current.observe(node);
    },
    [loading, hasMore]
  );

  const toggleSelection = (carId: string) => {
    const listing = cars?.listings.find((c) => c.id === carId);
    if (!listing) return;
    setSelected((prev) => {
      if (prev.some((l) => l.id === carId))
        return prev.filter((l) => l.id !== carId);
      if (prev.length >= MAX_SELECTION) return prev;
      return [...prev, listing];
    });
  };

  const handleSendToDealers = async () => {
    if (selected.length === 0) return;
    if (!isAuthenticated) {
      navigate('/login', { state: { from: { pathname: '/cars', search } } });
      return;
    }
    setSending(true);
    try {
      // Pass along whatever search location was used (zip, or lat/lng from geolocation) so
      // buyer-mode comp sourcing at OTD-request time has a geo center — see
      // docs/manual-deal-workflow-findings.md §5 and backend/src/outreach/comps.ts.
      const deal = await dealsService.post({
        searchZip: params.zip,
        searchLat: params.latitude,
        searchLng: params.longitude,
      });
      const results = await Promise.allSettled(
        selected.map((car) =>
          offersService.post({
            dealId: deal.id,
            dealer: car.dealer?.name || 'Dealer',
            dealerEmail: car.dealer?.seller_email || '',
            dealerPhone: car.dealer?.phone || null,
            vehicle: {
              vin: car.vin,
              make: car.build?.make || '',
              model: car.build?.model || '',
              trim: car.build?.trim || '',
              version: car.build?.version || '',
              year: car.build?.year || 0,
              mileage: car.miles || 0,
              listPrice: car.price ?? null,
              msrp: car.msrp ?? null,
              photos: car.media?.photo_links || [],
              sourceId: car.id,
              sourceUrl: car.vdp_url,
              // Fallback geo center for comp sourcing when the deal itself has no location.
              dealerZip: car.dealer?.zip,
            },
          })
        )
      );
      const failed = results.filter((r) => r.status === 'rejected').length;
      if (failed) {
        enqueueSnackbar(
          `${failed} of ${selected.length} dealer requests could not be sent.`,
          {
            variant: 'warning',
          }
        );
      }
      sessionStorage.removeItem(SELECTION_KEY);
      navigate(`/deals/${deal.id}`);
    } catch (e: any) {
      enqueueSnackbar(
        e?.response?.data?.message ||
          'Something went wrong sending your request.',
        {
          variant: 'error',
        }
      );
    } finally {
      setSending(false);
    }
  };

  const filteredListings = useMemo(() => {
    if (!cars?.listings) return [];
    const filtered = cars.listings.filter((car) => {
      return (
        (!filters.bodyType || car.build?.body_type === filters.bodyType) &&
        (!filters.transmission ||
          car.build?.transmission === filters.transmission) &&
        (!filters.fuelType || car.build?.fuel_type === filters.fuelType) &&
        (!filters.exteriorColor ||
          car.exterior_color === filters.exteriorColor) &&
        (!filters.interiorColor ||
          car.interior_color === filters.interiorColor) &&
        (!filters.driveType || car.build?.drivetrain === filters.driveType) &&
        (!filters.isCertified ||
          car.is_certified?.toString() === filters.isCertified) &&
        (!hideHighMilesForAge ||
          fleetRisk(car.miles || 0, car.build?.year || 0) !== 'high') &&
        // We can only ask for offers from dealers we can email.
        Boolean(car.dealer?.seller_email)
      );
    });
    return filtered.sort((a, b) => {
      switch (sortBy) {
        case 'price-low':
          return (a.price || 0) - (b.price || 0);
        case 'price-high':
          return (b.price || 0) - (a.price || 0);
        case 'mileage-low':
          return (a.miles || 0) - (b.miles || 0);
        case 'mileage-high':
          return (b.miles || 0) - (a.miles || 0);
        case 'year-new':
          return (b.build?.year || 0) - (a.build?.year || 0);
        case 'miles-per-year-low':
          return (
            annualizedMiles(a.miles || 0, a.build?.year || 0) -
            annualizedMiles(b.miles || 0, b.build?.year || 0)
          );
        default:
          return (a.dist || 0) - (b.dist || 0);
      }
    });
  }, [cars?.listings, filters, sortBy, hideHighMilesForAge]);

  if (!cars && loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="80vh"
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!cars && error) {
    return (
      <Container maxWidth="md" sx={{ py: 6 }}>
        <Alert severity="error">{error}</Alert>
      </Container>
    );
  }

  const selectedIds = new Set(selected.map((l) => l.id));

  return (
    <Container maxWidth="lg">
      <Box py={4}>
        <Typography
          variant="h4"
          component="h1"
          sx={{ fontWeight: 'bold', textAlign: 'center' }}
        >
          Available Cars ({cars?.num_found ?? 0})
        </Typography>
        <Typography
          variant="body1"
          color="text.primary"
          textAlign="center"
          sx={{ mb: 4 }}
        >
          Select up to {MAX_SELECTION} listings and we&apos;ll ask each dealer
          for their best offer.
        </Typography>

        <Grid container spacing={3}>
          <Grid item xs={12} md={3}>
            <Box
              sx={{
                position: 'sticky',
                top: 100,
                maxHeight: 'calc(100vh - 48px)',
                overflow: 'auto',
              }}
            >
              <CarFilters
                filters={filters}
                filterOptions={filterOptions}
                onFilterChange={(event: any) =>
                  setFilters((prev) => ({
                    ...prev,
                    [event.target.name]: event.target.value,
                  }))
                }
              />
              <FormControlLabel
                sx={{ mt: 1 }}
                control={
                  <Checkbox
                    checked={hideHighMilesForAge}
                    onChange={(e) => setHideHighMilesForAge(e.target.checked)}
                  />
                }
                label="Hide high-miles-for-age"
              />
              <Box
                sx={{
                  mt: 3,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                }}
              >
                <Typography variant="body1" fontWeight={600}>
                  Selected
                </Typography>
                <Chip
                  label={`${selected.length} of ${MAX_SELECTION}`}
                  color={
                    selected.length === MAX_SELECTION ? 'warning' : 'primary'
                  }
                  sx={{ fontWeight: 'bold' }}
                />
              </Box>
              <LoadingButton
                variant="contained"
                color="primary"
                sx={{ mt: 2 }}
                fullWidth
                loading={sending}
                disabled={selected.length === 0}
                onClick={handleSendToDealers}
              >
                {isAuthenticated
                  ? 'Request offers'
                  : 'Log in to request offers'}
              </LoadingButton>
              {!isAuthenticated && selected.length > 0 && (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: 'block', mt: 1 }}
                >
                  Your selection is saved — sign in or create a free account to
                  send it.
                </Typography>
              )}
            </Box>
          </Grid>

          <Grid item xs={12} md={9}>
            <Box sx={{ mb: 3 }}>
              <FormControl size="small" sx={{ minWidth: 200 }}>
                <InputLabel>Sort By</InputLabel>
                <Select
                  value={sortBy}
                  label="Sort By"
                  onChange={(e) => setSortBy(e.target.value)}
                >
                  <MenuItem value="distance">Distance</MenuItem>
                  <MenuItem value="price-low">Price: Low to High</MenuItem>
                  <MenuItem value="price-high">Price: High to Low</MenuItem>
                  <MenuItem value="mileage-low">Mileage: Low to High</MenuItem>
                  <MenuItem value="mileage-high">Mileage: High to Low</MenuItem>
                  <MenuItem value="year-new">Newest Year</MenuItem>
                  <MenuItem value="miles-per-year-low">
                    Fewest miles per year
                  </MenuItem>
                </Select>
              </FormControl>
            </Box>
            {error && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}
            {filteredListings.length === 0 && !loading && (
              <Alert severity="info">
                No listings match. Try widening your radius or clearing filters.
              </Alert>
            )}
            <Grid container spacing={3}>
              {filteredListings.map((car, index) => (
                <Grid
                  item
                  xs={12}
                  key={car.id}
                  ref={
                    index === filteredListings.length - 1 && hasMore
                      ? lastCarElementRef
                      : undefined
                  }
                >
                  <CarListingRow
                    car={{
                      id: car.id,
                      vin: car.vin,
                      make: car.build?.make || '',
                      model: car.build?.model || '',
                      trim: car.build?.trim || '',
                      version: car.build?.version || '',
                      photos: car.media?.photo_links || [],
                      year: car.build?.year || 0,
                      mileage: car.miles || 0,
                      listPrice: car.price ?? null,
                      msrp: car.msrp ?? null,
                      carfax1Owner: car.carfax_1_owner ?? null,
                    }}
                    dealer={{
                      name: car.dealer.name,
                      city: car.dealer.city,
                      state: car.dealer.state,
                      dist: car.dist,
                    }}
                    isSelected={selectedIds.has(car.id)}
                    selectionDisabled={selected.length >= MAX_SELECTION}
                    onSelect={toggleSelection}
                    onShowDetails={() => carDetailsModal.show({ car })}
                  />
                </Grid>
              ))}
            </Grid>
          </Grid>
        </Grid>

        {loading && (
          <Box display="flex" justifyContent="center" mt={4}>
            <CircularProgress />
          </Box>
        )}
      </Box>
    </Container>
  );
};

/** Remount on every new search so pagination and results start clean. */
const CarsPage = () => {
  const { search } = useLocation();
  return <Cars key={search} />;
};

export default CarsPage;
